// src/app/services/data-remote.service.ts
// This file implements remote database storage using central Rest-API based on Python and CouchDB

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, throwError, lastValueFrom, firstValueFrom, of } from 'rxjs';
import { catchError, map, timeout, retry, tap, switchMap } from 'rxjs/operators';
import { DataOfflineService } from './data-offline.service';

import {
  Note,
  Category,
  ApiResponse,
  BulkCategoryResult,
  BulkStatusResult,
  BulkDeleteResult,
  BulkTagAddResult,
  BulkTagRemoveResult,
  DbInfo,
} from './data.service';
import { LogService } from '../_service/log.service';
import { environment } from '../../environments/environment';
import { getOrderedRelatedNoteIds, toRelatedNotesPayload } from '../_util/related-notes.util';

// CouchDB writable fields for Note records
export const NOTE_WRITABLE_FIELDS = [
  '_id',
  '_rev',
  'type',
  'NoteType',
  'Title',
  'DescriptionHtml',
  'CommentsHtml',
  'Status',
  'Keywords',
  'AiKeywords',
  'Tags',
  'Categories',
  'ReferenceDate',
  'StartDate',
  'EndDate',
  'created_on',
  'last_modified',
  'Confidential',
  'templateName',
  'Attachments',
  'RelatedNotes',
] as const;

@Injectable({
  providedIn: 'root',
})
export class DataRemoteService {
  // Prefer env URL, but fall back to same-origin /api (useful if env file is missing)
  private readonly baseUrl = environment.apiBaseUrl || '/api';

  constructor(
    private http: HttpClient,
    private logs: LogService,
    private dataOfflineService: DataOfflineService,
  ) {}

  private toApiNoteId(id: string): string {
    const raw = String(id ?? '').trim();
    if (raw.startsWith('note:')) return raw;
    if (raw.startsWith('template:')) return raw;
    return `note:${raw}`;
  }

  private sanitizeNoteForRemote(note: Note): Partial<Note> {
    const { syncTime, syncStatus, remote_rev, id, Project, ProjectName, ...rest } = note as any;

    // filter only writable fields
    const payload = Object.fromEntries(
      Object.entries(rest).filter(([key]) => NOTE_WRITABLE_FIELDS.includes(key as any))
    ) as Partial<Note>;

    // The backend's RelatedNotes field is a plain array of id strings; it has no
    // concept of an explicit SortOrder. We don't need it to: CouchDB/JSON arrays
    // preserve element order end-to-end, so sending the ids in our locally-sorted
    // order (and rebuilding sequential SortOrder values from that same order on
    // the way back in, see normalizeNoteFromResponse) round-trips the ordering
    // without any backend schema change.
    const rawRelated = Array.isArray((note as any).RelatedNotes)
      ? (note as any).RelatedNotes
      : Array.isArray((note as any).relatedNotes)
        ? (note as any).relatedNotes
        : [];
    (payload as any).RelatedNotes = getOrderedRelatedNoteIds(rawRelated);

    if (!payload._rev) {
      delete payload._rev;
    }

    return payload;
  }

  private normalizeNoteFromResponse(note: Note, res: any): Note {
    const responseData = res?.status === 'success' ? res.data : res;
    const rawRelated = Array.isArray(responseData?.RelatedNotes)
      ? responseData.RelatedNotes
      : Array.isArray(responseData?.relatedNotes)
        ? responseData.relatedNotes
        : undefined;
    const updatedNote: Note = {
      ...note,
      ...(responseData ?? {}),
    };
    if (rawRelated !== undefined) {
      // The backend echoes back a plain, ordered array of ids; that order IS the
      // sort order, so rebuild explicit sequential SortOrder values from it.
      updatedNote.RelatedNotes = toRelatedNotesPayload(getOrderedRelatedNoteIds(rawRelated));
    }
    if (!updatedNote._id) updatedNote._id = note._id;
    return updatedNote;
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders();
    return token ? headers.set('Authorization', `Bearer ${token}`) : headers;
  }

  private toReadableError(error: unknown): Error {
    if (error instanceof HttpErrorResponse) {
      return new Error(this.describeHttpError(error));
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error(String(error));
  }

  private describeHttpError(error: HttpErrorResponse): string {
    if (error.status === 0) {
      const target = error.url ?? this.baseUrl;
      return `Cannot reach remote notes API at ${target}. This is usually caused by network connectivity, CORS, or a TLS certificate problem.`;
    }

    if (error.status === 401) return 'Authentication failed. Check your sign-in session.';
    if (error.status === 404) return 'Remote note API endpoint or document not found.';
    if (error.status === 409) return 'Document conflict. Document may have been modified by another user.';
    if (error.status >= 500) return 'Remote notes API server error.';

    return error.message || 'Remote notes API request failed.';
  }

  //#############################################################################
  // SYNCH OPERATIONS
  //#############################################################################


  // ✅ Manual + programmatic sync entry point
  // Used by:
  // - Home "Synch Now"
  // - online re-entry hook
//   syncAllChanges(
//     reason: 'manual' | 'online' | 'debug' = 'manual',
//   ): Promise<boolean> {
//     return this.syncCoordinator.forceSync(reason);
//   }

  // ✅ If you already react to network changes, keep it — just delegate
//   onOnlineStatusChange(isOnline: boolean): void {
//     if (isOnline) {
//       void this.syncAllChanges('online');
//     }
//   }

  /**
   * IMPORTANT: Your backend /changes uses include_docs=False,
   * so each row is id-only: {id, changes, deleted}. There is NO "doc".
   */
  async getCouchDBChanges(
    since: string
  ): Promise<{ results: Array<{ id: string; changes?: any[]; deleted?: boolean }>; last_seq: string }> {
    const safeSince = since || '0';

    // include_docs param is ignored by your backend (it forces include_docs=False),
    // but keep it out to avoid confusion.
    const url = `${this.baseUrl}/notes/changes?since=${encodeURIComponent(safeSince)}`;
    const headers = this.getAuthHeaders();

    console.log(`[CouchDB Sync] Fetching changes from: ${url}`);
    console.log('[CouchDB Sync] since:', safeSince);

    try {
      const rawResponse = await lastValueFrom(this.http.get<any>(url, { headers }));

      // support wrapped + raw
      const data = rawResponse?.status === 'success' ? rawResponse.data : rawResponse;

      if (!data || !Array.isArray(data.results)) {
        this.logs.append('synch', 'Error - invalid changes response structure from server!');
        throw new Error('Malformed changes response');
      }

      const lastSeq =
        typeof data.last_seq === 'number' || typeof data.last_seq === 'string'
          ? String(data.last_seq)
          : safeSince;

      // Filter notes/templates by ID prefix (matches your backend filter)
      const filtered = (data.results as any[])
        .map((row) => ({
          id: String(row?.id ?? ''),
          changes: row?.changes,
          deleted: row?.deleted === true,
        }))
        .filter((row) => row.id.startsWith('note:') || row.id.startsWith('template:'));

      return { results: filtered, last_seq: lastSeq };
    } catch (error: any) {
      console.error('[CouchDB Sync] Error fetching changes:', error);
      this.logs.append('synch', 'Error fetching CouchDB changes: ', error?.message ?? String(error));
      // SAFE fallback: do not advance sequence
      return { results: [], last_seq: safeSince };
    }
  }

  /** Hits /notes/info and /categories/info and logs their active counts into the "synch" log. */
  fetchDbInfoAndLogActive(): void {
    const notes$ = this.http
      .get<ApiResponse<any>>(`${this.baseUrl}/notes/info`, { headers: this.getAuthHeaders() })
      .pipe(
        catchError((err) =>
          of<ApiResponse<any>>({
            status: 'error',
            error: { code: err?.status ?? 0, message: err?.message ?? 'Notes info request failed' },
          })
        )
      );

    const categories$ = this.http
      .get<ApiResponse<any>>(`${this.baseUrl}/categories/info`, { headers: this.getAuthHeaders() })
      .pipe(
        catchError((err) =>
          of<ApiResponse<any>>({
            status: 'error',
            error: { code: err?.status ?? 0, message: err?.message ?? 'Categories info request failed' },
          })
        )
      );

    forkJoin({ notes: notes$, categories: categories$ })
      .pipe(
        tap(({ notes, categories }) => {
          if (notes.status === 'success' && notes.data) {
            this.logs.append('synch', 'Notes active in CouchDB:', String(notes.data.notes_active));
            this.logs.append('synch', 'Notes total in CouchDB:', String(notes.data.notes_total));
            this.logs.append('synch', 'Notes deleted in CouchDB:', String(notes.data.notes_deleted));
            this.logs.append('synch', 'Notes update_seq:', String(notes.data.update_seq));
          } else {
            this.logs.append('synch', 'CouchDB error getting notes statistics:', notes.error?.message ?? 'Unknown error');
          }

          if (categories.status === 'success' && categories.data) {
            this.logs.append(
              'synch',
              'Categories active in CouchDB:',
              String(categories.data.categories_active ?? categories.data.active)
            );
          } else {
            this.logs.append(
              'synch',
              'CouchDB error getting categories statistics:',
              categories.error?.message ?? 'Unknown error'
            );
          }
        })
      )
      .subscribe();
  }

  getNotesInfo(): Observable<ApiResponse<DbInfo>> {
    return this.http
      .get<ApiResponse<DbInfo>>(`${this.baseUrl}/notes/info`, { headers: this.getAuthHeaders() })
      .pipe(
        catchError((err) =>
          of<ApiResponse<DbInfo>>({
            status: 'error',
            error: { code: err?.status ?? 0, message: err?.message ?? 'Notes info request failed' },
          }),
        ),
      );
  }


  /**
 * Compatibility wrapper used by older code paths.
 * Delegates to updateNote() and normalizes result to ApiResponse<Note>.
 */
updateNoteIgnoreConflict(
  note: Note
): Observable<ApiResponse<Note>> {
  return new Observable<ApiResponse<Note>>((observer) => {
    this.updateNote(note)
      .then((updatedNote) => {
        observer.next({
          status: 'success',
          data: updatedNote,
        });
        observer.complete();
      })
      .catch((err) => {
        observer.next({
          status: 'error',
          error: {
            code: err?.status ?? 500,
            message: err?.message ?? 'Update failed',
          },
        });
        observer.complete();
      });
  });
}

  //#############################################################################
  // CONFLICT RESOLUTION OPERATIONS
  //#############################################################################

  putNoteToCouchDB(note: Note): Observable<ApiResponse<Note>> {
    if (!note || !note._id) {
      return of({ status: 'error' as const, error: { code: 400, message: 'Invalid note object' } });
    }

    const apiNoteId = this.toApiNoteId(note._id);
    const url = `${this.baseUrl}/notes/${encodeURIComponent(apiNoteId)}`;
    const headers = this.getAuthHeaders();
    const payload = this.sanitizeNoteForRemote(note);

    return this.http.put<ApiResponse<Note>>(url, payload, { headers }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) {
          this.logs.append('synch', `[PUT] Updated note ${note._id} successfully via Flask API.`);
          return { status: 'success' as const, data: response.data };
        }
        const msg = response.error?.message ?? 'Unknown backend error';
        this.logs.append('synch', `[PUT] Failed for note ${note._id}: ${msg}`);
        return { status: 'error' as const, error: { code: 500, message: msg } };
      }),
      catchError((err: any) =>
        of({ status: 'error' as const, error: { code: err.status ?? 0, message: err.message ?? 'PUT request failed' } })
      )
    );
  }

  cleanupCouchDBConflicts(noteId: string): Observable<ApiResponse<null>> {
    if (!noteId) return of({ status: 'error' as const, error: { code: 400, message: 'Missing noteId' } });

    const apiNoteId = this.toApiNoteId(noteId);
    const url = `${this.baseUrl}/notes/${encodeURIComponent(apiNoteId)}/cleanup`;
    const headers = this.getAuthHeaders();

    return this.http.delete<ApiResponse<null>>(url, { headers }).pipe(
      map((response) => {
        if (response.status === 'success') return { status: 'success' as const, data: null };
        const msg = response.error?.message ?? 'Unknown cleanup error';
        return { status: 'error' as const, error: { code: 500, message: msg } };
      }),
      catchError((err: any) =>
        of({ status: 'error' as const, error: { code: err.status ?? 0, message: err.message ?? 'Cleanup request failed' } })
      )
    );
  }

  //#############################################################################
  // READ-ONLY OPERATIONS
  //#############################################################################

  checkConnection(): Observable<any> {
    return this.http.get(`${this.baseUrl}/`, { headers: this.getAuthHeaders() }).pipe(timeout(5000), retry(1), catchError(this.handleError));
  }

  getAllNotes(): Observable<Note[]> {
    const url = `${this.baseUrl}/notes`;
    return this.http.get<ApiResponse<Note[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError.bind(this))
    );
  }

  getTemplates(): Observable<Note[]> {
    const url = `${this.baseUrl}/notes/templates`;
    return this.http.get<ApiResponse<Note[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error');
      }),
      catchError(this.handleError.bind(this))
    );
  }

  getNote(_id: string): Observable<Note> {
    const apiNoteId = this.toApiNoteId(_id);
    const url = `${this.baseUrl}/notes/${encodeURIComponent(apiNoteId)}`;
    return this.http.get<ApiResponse<Note>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        const payload = response?.status === 'success' ? response.data : (response as any);
        if (payload && payload._id) return payload as Note;
        throw new Error(response?.error?.message || 'Unknown error');
      }),
      catchError((error) => this.handleError(error))
    );
  }

  getAllCategories(): Observable<Category[]> {
    const url = `${this.baseUrl}/categories`;
    return this.http.get<ApiResponse<Category[]>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) {
          const normalized = response.data.map((category) => this.normalizeCategory(category));
          console.log('[DataRemoteService] getAllCategories: received', normalized.length, 'categories');
          if (normalized.length > 0) {
            console.log('[DataRemoteService] getAllCategories: sample category', normalized[0]);
          }
          return normalized;
        }
        console.error('[DataRemoteService] getAllCategories: error response', response);
        throw new Error(response.error?.message || 'Unknown error fetching categories');
      }),
      catchError((err) => {
        console.error('[DataRemoteService] getAllCategories: request failed', err);
        return this.handleError(err);
      })
    );
  }

  //#############################################################################
  // CREATE / UPDATE / DELETE OPERATIONS
  //#############################################################################

  importNoteFromOffline(note: Note): Observable<Note> {
    const url = `${this.baseUrl}/notes/import`;
    if (!note._id) return throwError(() => new Error('Note ID is required for creation'));

    const payload: Note = {
      ...(this.sanitizeNoteForRemote(note) as Note),
      last_modified: new Date().toISOString(),
    };
    
    // CRITICAL: Remove _rev for new note creation to avoid 409 conflicts
    delete (payload as any)._rev;

    return this.http.post<ApiResponse<Note>>(url, payload, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error during import');
      }),
      timeout(5000),
      retry(1),
      catchError((err: HttpErrorResponse) => {
        // If we get a 409 conflict, the document already exists on the server.
        // This can happen if a previous save succeeded but the response wasn't received
        // (e.g. timeout), or if the background sync already pushed the note.
        // Recover by fetching the server's current _rev and re-submitting the LOCAL
        // content as a PUT, so the user's edits are never lost.
        if (err.status === 409) {
          console.warn('⚠️ Import conflict (409) - document already exists. Retrying as update with local content...');
          const putUrl = `${this.baseUrl}/notes/${encodeURIComponent(this.toApiNoteId(note._id))}`;
          return this.getNote(note._id).pipe(
            switchMap((existingNote) => {
              if (!existingNote?._rev) {
                throw new Error('Conflict: server document could not be retrieved');
              }
              const retryPayload: Note = {
                ...(this.sanitizeNoteForRemote(note) as Note),
                _rev: existingNote._rev,
                last_modified: new Date().toISOString(),
              };
              return this.http.put<ApiResponse<Note>>(putUrl, retryPayload, { headers: this.getAuthHeaders() }).pipe(
                map((response) => {
                  const data = response?.status === 'success' ? response.data : (response as any);
                  return { ...note, _rev: data?._rev ?? existingNote._rev } as Note;
                }),
                catchError(() => of({ ...note, _rev: existingNote._rev } as Note)),
              );
            }),
          );
        }
        return this.handleError(err);
      })
    );
  }

  createNote(): Observable<ApiResponse<Note>> {
    const url = `${this.baseUrl}/notes`;
    return this.http.post<ApiResponse<Note>>(url, {}, { headers: this.getAuthHeaders() });
  }

bulkSetCategory(
  noteIds: string[],
  categoryId: string,
  updateTimestamp: boolean
): Observable<ApiResponse<BulkCategoryResult>> {
  const url = `${this.baseUrl}/notes/bulk/category`;

  const payload = {
    ids: noteIds,
    category_id: categoryId,
    update_timestamp: updateTimestamp,
  };

  return this.http
    .post<ApiResponse<BulkCategoryResult>>(url, payload, {
      headers: this.getAuthHeaders(),
    })
    .pipe(
      catchError((err) =>
        of<ApiResponse<BulkCategoryResult>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.message ?? 'Bulk category update failed',
          },
        })
      )
    );
}

bulkSetStatus(
  noteIds: string[],
  status: string,
  updateTimestamp: boolean
): Observable<ApiResponse<BulkStatusResult>> {
  const url = `${this.baseUrl}/notes/bulk/status`;

  const payload = {
    ids: noteIds,
    status,
    update_timestamp: updateTimestamp,
  };

  return this.http
    .post<ApiResponse<BulkStatusResult>>(url, payload, {
      headers: this.getAuthHeaders(),
    })
    .pipe(
      catchError((err) =>
        of<ApiResponse<BulkStatusResult>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.message ?? 'Bulk status update failed',
          },
        })
      )
    );
}
bulkDelete(
  noteIds: string[]
): Observable<ApiResponse<BulkDeleteResult>> {
  const url = `${this.baseUrl}/notes/bulk/delete`;

  const payload = {
    ids: noteIds,
  };

  return this.http
    .post<ApiResponse<BulkDeleteResult>>(url, payload, {
      headers: this.getAuthHeaders(),
    })
    .pipe(
      catchError((err) =>
        of<ApiResponse<BulkDeleteResult>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.message ?? 'Bulk delete failed',
          },
        })
      )
    );
}

bulkAddTags(
  noteIds: string[],
  tagIds: string[],
): Observable<ApiResponse<BulkTagAddResult>> {
  const url = `${this.baseUrl}/tags/notes/bulk-add`;

  const payload = {
    note_ids: noteIds,
    tag_ids: tagIds,
  };

  return this.http
    .post<ApiResponse<BulkTagAddResult>>(url, payload, {
      headers: this.getAuthHeaders(),
    })
    .pipe(
      catchError((err) =>
        of<ApiResponse<BulkTagAddResult>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.message ?? 'Bulk add tags failed',
          },
        })
      )
    );
}

bulkRemoveTags(
  noteIds: string[],
  tagIds: string[],
  removeAll: boolean,
): Observable<ApiResponse<BulkTagRemoveResult>> {
  const url = `${this.baseUrl}/tags/notes/bulk-remove`;

  const payload = removeAll
    ? {
        note_ids: noteIds,
        remove_all_tags: true,
      }
    : {
        note_ids: noteIds,
        tag_ids: tagIds,
      };

  return this.http
    .post<ApiResponse<BulkTagRemoveResult>>(url, payload, {
      headers: this.getAuthHeaders(),
    })
    .pipe(
      catchError((err) =>
        of<ApiResponse<BulkTagRemoveResult>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.message ?? 'Bulk remove tags failed',
          },
        })
      )
    );
}

  async updateNote(note: Note): Promise<Note> {
    const url = `${this.baseUrl}/notes/${encodeURIComponent(this.toApiNoteId(note._id))}`;
    const headers = this.getAuthHeaders();

    if (!note._rev) {
      try {
        const getRes: any = await firstValueFrom(this.http.get(url, { headers }));
        const latest = getRes?.data ?? getRes;
        if (latest?._rev) note._rev = latest._rev;
      } catch (err: any) {
        if (err?.status === 404) {
          const imported = await firstValueFrom(this.importNoteFromOffline(note));
          if (imported?._rev) note._rev = imported._rev;
          await this.dataOfflineService.saveNote(note, true);
          return imported;
        }
        throw this.toReadableError(err);
      }
    }

    const payload: Note = {
      ...(this.sanitizeNoteForRemote(note) as Note),
      last_modified: new Date().toISOString(),
    };

    try {
      const res: any = await firstValueFrom(this.http.put(url, payload, { headers }));

      const updatedNote = this.normalizeNoteFromResponse(note, res);
      if (updatedNote._rev) note._rev = updatedNote._rev;

      // Keep IndexedDB in sync (mark synced)
      await this.dataOfflineService.saveNote(updatedNote, true);

      return updatedNote;
    } catch (err: any) {
      if (err.status === 409) {
        const getRes: any = await firstValueFrom(this.http.get(url, { headers }));
        const latest = getRes?.data;
        if (!latest || !latest._rev) throw new Error('Could not fetch latest revision for retry');

        const retryPayload = { ...payload, _rev: latest._rev };
        const retryRes: any = await firstValueFrom(this.http.put(url, retryPayload, { headers }));

        const updatedNote = this.normalizeNoteFromResponse(note, retryRes);
        if (updatedNote._rev) note._rev = updatedNote._rev;

        await this.dataOfflineService.saveNote(updatedNote, true);

        return updatedNote;
      }
      throw this.toReadableError(err);
    }
  }

  deleteNote(_id: string, rev: string): Observable<{ _id: string; status: string }> {
    const apiNoteId = this.toApiNoteId(_id);
    const url = `${this.baseUrl}/notes/${encodeURIComponent(apiNoteId)}`;
    return this.http.delete<ApiResponse<{ _id: string; status: string }>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((response) => {
        if (response.status === 'success' && response.data) return response.data;
        throw new Error(response.error?.message || 'Unknown error during note deletion');
      }),
      catchError(this.handleError)
    );
  }

  // -----------------------------
  // Categories
  // -----------------------------

  createCategory(payload: {
    name?: string;
    Name?: string;
    ShortName?: string;
    Color?: string;
    Icon?: string;
    Owner?: string;
    SortOrder?: number;
    parent_id: string | null;
    _id?: string;
  }): Observable<ApiResponse<Category>> {
    const url = `${this.baseUrl}/categories`;
    const body: any = { name: payload.name ?? payload.Name, parent_id: payload.parent_id };
    if (payload.ShortName !== undefined) body.ShortName = payload.ShortName;
    if (payload.Color !== undefined) body.Color = payload.Color;
    if (payload.Icon !== undefined) body.Icon = payload.Icon;
    if (payload.Owner !== undefined) body.Owner = payload.Owner;
    if (payload.SortOrder !== undefined) body.SortOrder = payload.SortOrder;
    if (payload._id !== undefined) body._id = payload._id;

    return this.http.post<ApiResponse<Category>>(url, body, { headers: this.getAuthHeaders() }).pipe(
      timeout(5000),
      retry(1),
      map((response) => {
        if (response.status === 'success' && response.data) {
          response.data = this.normalizeCategory(response.data);
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  updateCategory(category: Partial<Category> & { _id: string }): Observable<ApiResponse<Category>> {
    if (!category._id) return throwError(() => new Error('Category ID is required for update'));

    const body: any = {};
    if (category.Name !== undefined) body.name = category.Name;
    if ('parent_id' in category) body.parent_id = category.parent_id;
    if ('Parent' in category) body.parent_id = category.Parent;
    if (category.ShortName !== undefined) body.short_name = category.ShortName;
    if (category.Color !== undefined) body.color = category.Color;
    if (category.Icon !== undefined) body.icon = category.Icon;
    if (category.Owner !== undefined) body.owner = category.Owner;
    if (category.SortOrder !== undefined) body.sort_order = category.SortOrder;
    if (category._id !== undefined) body._id = category._id;

    const url = `${this.baseUrl}/categories/${category._id}`;
    return this.http.put<ApiResponse<Category>>(url, body, { headers: this.getAuthHeaders() }).pipe(
      timeout(5000),
      retry(1),
      map((response) => {
        if (response.status === 'success' && response.data) {
          response.data = this.normalizeCategory(response.data);
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  private normalizeCategory(raw: Category): Category {
    const data = raw as any;
    const normalized: Category = {
      _id: data._id,
      Name: data.Name ?? data.name ?? '',
      Parent: data.Parent ?? data.parent_id ?? null,
      LeftValue: data.LeftValue ?? data.left ?? 0,
      RightValue: data.RightValue ?? data.right ?? 0,
      Path: data.Path ?? data.path ?? '',
      ShortName: data.ShortName ?? data.short_name,
      Color: data.Color ?? data.color,
      Icon: data.Icon ?? data.icon,
      Owner: data.Owner ?? data.owner,
      SortOrder: data.SortOrder ?? data.sort_order,
    };
    return normalized;
  }

  deleteCategory(id: string, mode: 'subtree' | 'prune' = 'subtree'): Observable<{ _id: string; status: string }> {
    const url = `${this.baseUrl}/categories/${id}?mode=${mode}`;
    return this.http.delete<ApiResponse<{ _id: string; status: string } | undefined>>(url, { headers: this.getAuthHeaders() }).pipe(
      map((resp) => {
        if (resp.status === 'success') return resp.data ?? { _id: id, status: 'success' };
        throw new Error(resp.error?.message || 'Unknown error during category deletion');
      }),
      timeout(5000),
      retry(1),
      catchError(this.handleError)
    );
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    return throwError(() => new Error(this.describeHttpError(error)));
  };
}
